import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, PropsWithChildren, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStandard, ButtonStandardType } from '../../button/ButtonStandard/ButtonStandard';
import { GlobalConfirmDeleteDialogContext, GlobalConfirmDeleteDialogContextOpenOptions, GlobalConfirmDeleteDialogContextValue } from '../../context/GlobalConfirmDeleteDialogContext';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useModalOpened } from '../../hooks/useModalOpened/useModalOpened';
import { prepareProps } from '../../utils/propUtils';
import { ConfirmModal, ConfirmModalWidthType } from '../ConfirmModal/ConfirmModal';
import { ConfirmModalBody } from '../ConfirmModalBody/ConfirmModalBody';
import { ConfirmModalButtonFooter } from '../ConfirmModalButtonFooter/ConfirmModalButtonFooter';
import { ConfirmModalHeader } from '../ConfirmModalHeader/ConfirmModalHeader';
export type GlobalConfirmDeleteDialogProviderProps = PropsWithChildren<Record<never, never>>;
export const GlobalConfirmDeleteDialogProvider: FunctionComponent<GlobalConfirmDeleteDialogProviderProps> = React.memo(props => {
  const $ = _c(41);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    children
  } = t0;
  const [options, setOptions] = useState(null);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      closeOnBackDropClick: true
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    open: openModal,
    close: closeModal,
    modalProps
  } = useModalOpened(t1);
  const {
    t
  } = useTranslation("record-it-components");
  let t2;
  if ($[3] !== openModal) {
    t2 = opts => {
      setOptions(opts as GlobalConfirmDeleteDialogContextOpenOptions<unknown>);
      openModal();
    };
    $[3] = openModal;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const open = useEventCallback(t2);
  let t3;
  if ($[5] !== closeModal || $[6] !== options) {
    t3 = () => {
      options?.onDelete(options.item);
      closeModal();
    };
    $[5] = closeModal;
    $[6] = options;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleDeleteClick = useEventCallback(t3);
  let t4;
  if ($[8] !== open) {
    t4 = {
      open
    };
    $[8] = open;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const contextValue = t4;
  let t5;
  if ($[10] !== t) {
    t5 = t("modal.GlobalConfirmDeleteDialogProvider.title");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <ConfirmModalHeader>{t5}</ConfirmModalHeader>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== options || $[15] !== t) {
    t7 = t("modal.GlobalConfirmDeleteDialogProvider.message", {
      name: options?.getName(options.item)
    });
    $[14] = options;
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t7) {
    t8 = <ConfirmModalBody>{t7}</ConfirmModalBody>;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t) {
    t9 = t("modal.GlobalConfirmDeleteDialogProvider.delete");
    $[19] = t;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== handleDeleteClick || $[22] !== t9) {
    t10 = <ButtonStandard type={ButtonStandardType.Danger} onClick={handleDeleteClick} data-testid="GlobalConfirmDialogProvider.DeleteButton">{t9}</ButtonStandard>;
    $[21] = handleDeleteClick;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t) {
    t11 = t("modal.GlobalConfirmDeleteDialogProvider.cancel");
    $[24] = t;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== closeModal || $[27] !== t11) {
    t12 = <ButtonStandard type={ButtonStandardType.Secondary} onClick={closeModal} data-testid="GlobalConfirmDialogProvider.CancelButton">{t11}</ButtonStandard>;
    $[26] = closeModal;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t10 || $[30] !== t12) {
    t13 = <ConfirmModalButtonFooter>{t10}{t12}</ConfirmModalButtonFooter>;
    $[29] = t10;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== modalProps || $[33] !== t13 || $[34] !== t6 || $[35] !== t8) {
    t14 = <ConfirmModal {...modalProps} widthType={ConfirmModalWidthType.Auto} data-testid="GlobalConfirmDialogProvider.Modal">{t6}{t8}{t13}</ConfirmModal>;
    $[32] = modalProps;
    $[33] = t13;
    $[34] = t6;
    $[35] = t8;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== children || $[38] !== contextValue || $[39] !== t14) {
    t15 = <GlobalConfirmDeleteDialogContext.Provider value={contextValue}>{children}{t14}</GlobalConfirmDeleteDialogContext.Provider>;
    $[37] = children;
    $[38] = contextValue;
    $[39] = t14;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  return t15;
});